// Mock data for the Gifts Review screen (approved HTML:
// 07-Gifts-Review-v2-Clean.html). Catalog items mirror
// dist/account-workflows.GIFT_CATALOG so the UI represents what the
// backend will enforce once wired. Variants for Design 01/02/03 extend
// the simple catalog (shirts & caps have brand design variants; half
// and full gallons map to product line names).
//
// TODO(runtime): swap mock -> real API once services land:
//   - gift catalog: expose GIFT_CATALOG via a read endpoint
//   - gift eligibility: dist/account-workflows.giftEligibility(state, cid)
//     already models 90-day cadence, pending-gift blocking, service-gate
//   - upcoming shipments: dist/account-workflows.subscriptions

export type GiftCategoryKey = 'shirts' | 'caps' | 'half' | 'full';

export interface GiftVariant {
  id: string;
  name: string;
  variant: string;
  tone: 'navy' | 'charcoal' | 'sand' | 'gold' | 'teal' | 'blue' | 'purple' | 'aqua';
  kind: 'T-SHIRT' | 'BALL CAP' | 'HALF GALLON' | 'FULL GALLON';
  monogram?: string;
  catalogId: string;
  available: boolean;
}

export interface UpcomingShipment {
  id: string;
  label: string;
  products: string;
  monthLabel: string;
  dayLabel: string;
  summary: string;
}

export const UPCOMING_SHIPMENTS: UpcomingShipment[] = [
  {
    id: 'barn-oct-15',
    label: 'Barn supplements',
    products: 'Gut X + Osteo-MAX',
    monthLabel: 'Oct 15',
    dayLabel: 'Example order date',
    summary: 'Barn supplements · October 15',
  },
  {
    id: 'hydration-oct-28',
    label: 'Hydration',
    products: 'Hydra-MAX',
    monthLabel: 'Oct 28',
    dayLabel: 'Example order date',
    summary: 'Hydration · October 28',
  },
];

// Shirt and cap designs are mock brand design families; variant IDs are
// local to this mock. The 100X T-shirt catalog item in GIFT_CATALOG maps
// to catalogId=sample-shirt; the ball cap maps to catalogId=sample-cap
// (stock=0, so all cap variants render Out of stock). Half-gallon product
// variants fold into catalogId=sample-gutx-half or sample-hydra-trial.
const SHIRT_VARIANTS: GiftVariant[] = [
  {
    id: 'shirt1',
    name: '100X T-shirt',
    variant: 'Design 01 · Navy',
    tone: 'navy',
    kind: 'T-SHIRT',
    catalogId: 'sample-shirt',
    available: true,
  },
  {
    id: 'shirt2',
    name: '100X T-shirt',
    variant: 'Design 02 · Charcoal',
    tone: 'charcoal',
    kind: 'T-SHIRT',
    catalogId: 'sample-shirt',
    available: true,
  },
  {
    id: 'shirt3',
    name: '100X T-shirt',
    variant: 'Design 03 · Sand',
    tone: 'sand',
    kind: 'T-SHIRT',
    catalogId: 'sample-shirt',
    available: false,
  },
];

const CAP_VARIANTS: GiftVariant[] = [
  {
    id: 'cap1',
    name: '100X ball cap',
    variant: 'Design 01 · Navy',
    tone: 'navy',
    kind: 'BALL CAP',
    catalogId: 'sample-cap',
    available: true,
  },
  {
    id: 'cap2',
    name: '100X ball cap',
    variant: 'Design 02 · Charcoal',
    tone: 'charcoal',
    kind: 'BALL CAP',
    catalogId: 'sample-cap',
    available: true,
  },
  {
    id: 'cap3',
    name: '100X ball cap',
    variant: 'Design 03 · Sand',
    tone: 'sand',
    kind: 'BALL CAP',
    catalogId: 'sample-cap',
    available: false,
  },
];

const PRODUCT_LINES: Array<{ name: string; tone: GiftVariant['tone']; monogram: string }> = [
  { name: 'Gut X', tone: 'gold', monogram: 'GX' },
  { name: 'Osteo-MAX', tone: 'teal', monogram: 'OM' },
  { name: 'Joint Flex Plus', tone: 'blue', monogram: 'JF' },
  { name: 'Amino-MAX', tone: 'purple', monogram: 'AM' },
  { name: 'Hydra-MAX', tone: 'aqua', monogram: 'HM' },
  { name: 'Max Calm', tone: 'sand', monogram: 'MC' },
];

const HALF_VARIANTS: GiftVariant[] = PRODUCT_LINES.map((p, i) => ({
  id: `half${i}`,
  name: p.name,
  variant: '½ gallon',
  tone: p.tone,
  monogram: p.monogram,
  kind: 'HALF GALLON',
  catalogId: p.name === 'Gut X' ? 'sample-gutx-half' : 'sample-hydra-trial',
  available: i !== 3,
}));

const FULL_VARIANTS: GiftVariant[] = PRODUCT_LINES.map((p, i) => ({
  id: `full${i}`,
  name: p.name,
  variant: '1 gallon',
  tone: p.tone,
  monogram: p.monogram,
  kind: 'FULL GALLON',
  catalogId: p.name === 'Gut X' ? 'sample-gutx-half' : 'sample-hydra-trial',
  available: i !== 5,
}));

export const GIFT_CATALOG_BY_CATEGORY: Record<GiftCategoryKey, GiftVariant[]> = {
  shirts: SHIRT_VARIANTS,
  caps: CAP_VARIANTS,
  half: HALF_VARIANTS,
  full: FULL_VARIANTS,
};

export const CATEGORY_LABELS: Record<GiftCategoryKey, { title: string; description: string }> = {
  shirts: {
    title: 'Choose a T-shirt design',
    description: 'Select a design, then choose the size.',
  },
  caps: {
    title: 'Choose a ball cap design',
    description: 'Select the exact cap design and color.',
  },
  half: {
    title: 'Choose a half-gallon product',
    description: 'The selected gift is a half-gallon variant.',
  },
  full: {
    title: 'Choose a full-gallon product',
    description: 'The selected gift is a full-gallon variant.',
  },
};

export const SHIRT_SIZES = ['S', 'M', 'L', 'XL', '2XL'] as const;
